import { Form, Formik } from 'formik';
import { Input, Select, Switch, Alert } from 'antd';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SuperAdminTable } from '../../../baseComponents/Table';
import { getOrgSetting } from 'src/store/actions/settings.action';
import { getPractitionersOrgLevel } from 'src/modules/SuperAdmin/store/actions/practitioner.action';
import { getFromStorage } from '../../../../../../lib/storage';
import organizationEventEmitter from '../../../../../../utilities/organizationEmmiter';
import { SaveOutlined } from '@ant-design/icons';
import * as Yup from 'yup';
import {
  getDACCSVSettings,
  saveDACCSVSettings,
  disableDACCSVSettings,
} from 'src/modules/SuperAdmin/store/actions/organization.action';
import { showToastSuccess } from 'src/utilities/toast';

const DACCSVSettings = () => {
  const dispatch = useDispatch();
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const { dacCSVSettings, dacCSVSettingsLoading } = useSelector(
    (state) => state?.organizationState
  );
  const [DACCSVSettingsEnabled, setDACCSVSettingsEnabled] = useState(false);
  const [tableLoader, setTableLoader] = useState(false);
  const [initialValues, setInitialValues] = useState({
    practitioner: dacCSVSettings?.practitioner || '',
    time_period: dacCSVSettings?.time_period || '',
    timestamp_field: dacCSVSettings?.timestamp_field || '',
  });
  const [bulkCSVUploadEnabled, setBulkCSVUploadEnabled] = useState(
    currentPractitioner?.org_settings?.enable_bulk_appointment_upload ?? false
  );
  const [selectedPractitioner, setSelectedPractitioner] = useState('');
  const [selectedTimePeriod, setSelectedTimePeriod] = useState('');
  const [selectedTimestampField, setSelectedTimestampField] = useState('');
  const [tableData, setTableData] = useState([
    {
      setting: 'Practitioner',
      value: 'practitioner',
    },
    {
      setting: 'Days from creation',
      value: 'time_period',
    },
    {
      setting: 'Reference time field',
      value: 'timestamp_field',
    },
  ]);

  const [selectedOrganizationUuid, setSelectedOrganizationUuid] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUuid(newOrg);
        handleSelectChange(newOrg);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleSelectChange = (option) => {
    const orgUuid = option?.org_uuid;
    setSelectedOrganizationUuid(orgUuid);
  };

  useEffect(() => {
    setTableLoader(dacCSVSettingsLoading);
    if (!dacCSVSettingsLoading) {
      setInitialValues({
        practitioner: dacCSVSettings?.practitioner ?? '',
        time_period: dacCSVSettings?.time_period ?? '',
        timestamp_field: dacCSVSettings?.timestamp_field ?? '',
      });
      setSelectedTimePeriod(dacCSVSettings?.time_period ?? '');
      setSelectedPractitioner(dacCSVSettings?.practitioner ?? '');
      setSelectedTimestampField(dacCSVSettings?.timestamp_field ?? '');
      setDACCSVSettingsEnabled(
        dacCSVSettings?.practitioner !== undefined &&
          dacCSVSettings?.time_period !== undefined &&
          dacCSVSettings?.timestamp_field !== undefined
      );
    }
  }, [dacCSVSettings, dacCSVSettingsLoading]);

  useEffect(() => {
    async function fetchOrgData() {
      const setting = await dispatch(getOrgSetting(selectedOrganizationUuid));
      setBulkCSVUploadEnabled(setting?.enable_bulk_appointment_upload ?? false);

      dispatch(getPractitionersOrgLevel(selectedOrganizationUuid));
      dispatch(getDACCSVSettings(selectedOrganizationUuid));
    }

    if (selectedOrganizationUuid) {
      fetchOrgData();
    }
  }, [selectedOrganizationUuid, dispatch]);

  const DACCSVSettingsSchema = Yup.object().shape({
    practitioner: Yup.number().required('Practitioner is required'),
    time_period: Yup.number().required('Time period is required'),
    timestamp_field: Yup.string().required('Reference time field is required'),
  });

  function hasMultiplePractitionersWithSameName(name) {
    const practitionersWithSameName = practitionersOrgLevel?.filter(
      (practitioner) => practitioner.practitioner_name === name
    );
    return practitionersWithSameName.length > 1;
  }

  const { practitionersOrgLevel } = useSelector((state) => state?.doctorsState);

  const handleRadioChange = (checked) => {
    setDACCSVSettingsEnabled(checked);
    if (!checked) {
      dispatch(disableDACCSVSettings(selectedOrganizationUuid));
      showToastSuccess('DAC CSV settings disabled');
    }
  };

  const handleFormSubmit = (values) => {
    const payload = {
      org_uuid: selectedOrganizationUuid,
      practitioner_id: values?.practitioner,
      start_time_period: parseInt(values?.time_period, 10),
      timestamp_field: values?.timestamp_field,
    };
    dispatch(saveDACCSVSettings(payload));
  };

  return bulkCSVUploadEnabled ? (
    <div>
      <Formik
        initialValues={initialValues}
        validationSchema={DACCSVSettingsSchema}
        onSubmit={handleFormSubmit}
      >
        {({ handleSubmit, setFieldValue, errors, touched }) => {
          const columns = [
            {
              title: 'Setting',
              dataIndex: 'setting',
              width: '50%',
              render: (text) => (
                <span>
                  {text} <span className="text-red-500">*</span>
                </span>
              ),
            },
            {
              title: 'Value',
              dataIndex: 'value',
              width: '50%',
              render: (text) => {
                return text === 'practitioner' ? (
                  <div>
                    <Select
                      style={{
                        placeholder: {
                          fontFamily: 'Poppins',
                          color: '#667085',
                        },
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                      }}
                      id="search_practitioner_Practitioner"
                      value={
                        selectedPractitioner
                          ? {
                              value: selectedPractitioner,
                              label: practitionersOrgLevel?.find(
                                (p) =>
                                  p.practitioner_id === selectedPractitioner
                              )?.practitioner_name,
                            }
                          : undefined
                      }
                      labelInValue
                      onChange={(value) => {
                        setSelectedPractitioner(value.value);
                        setFieldValue('practitioner', value.value);
                      }}
                      className="font-sans h-10 flex items-center w-full"
                      optionFilterProp="label"
                      placeholder="Select practitioner"
                      showSearch
                    >
                      {practitionersOrgLevel?.map((names, idx) => {
                        const nameWithoutTitle =
                          names.practitioner_name?.replace('Dr. ', '');
                        const [firstName, lastName] =
                          nameWithoutTitle.split(' ');
                        return (
                          <Select.Option
                            key={idx}
                            value={names.practitioner_id}
                            label={names.practitioner_name}
                          >
                            <div className="font-sans flex">
                              <div className="flex items-center">
                                <div className="">
                                  {names.practitioner_name}
                                  {hasMultiplePractitionersWithSameName(
                                    names.practitioner_name
                                  ) && (
                                    <div
                                      style={{
                                        color: 'grey',
                                        fontSize: 'smaller',
                                        fontFamily: 'Poppins',
                                      }}
                                    >
                                      <span>{names.practitioner_email}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </Select.Option>
                        );
                      })}
                    </Select>
                    {errors.practitioner && touched.practitioner && (
                      <div className="text-red-500 font-sans text-sm mt-1">
                        {errors.practitioner}
                      </div>
                    )}
                  </div>
                ) : text === 'time_period' ? (
                  <>
                    <Input
                      style={{
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                      }}
                      type="number"
                      placeholder="Enter time period"
                      className="h-10 flex items-center w-full"
                      onChange={(e) => {
                        setSelectedTimePeriod(e.target.value);
                        setFieldValue('time_period', e.target.value);
                      }}
                      value={selectedTimePeriod}
                    />
                    {errors.time_period && touched.time_period && (
                      <div className="text-red-500 font-sans text-sm mt-1">
                        {errors.time_period}
                      </div>
                    )}
                  </>
                ) : (
                  <div>
                    <Select
                      style={{
                        placeholder: {
                          fontFamily: 'Poppins',
                          color: '#667085',
                        },
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                      }}
                      placeholder="Select reference time field"
                      value={
                        selectedTimestampField !== ''
                          ? selectedTimestampField
                          : undefined
                      }
                      onChange={(value) => {
                        setSelectedTimestampField(value);
                        setFieldValue('timestamp_field', value);
                      }}
                      className="font-sans h-10 flex items-center w-full"
                      options={[
                        {
                          value: 'created_time',
                          label: 'Created Time',
                        },
                        {
                          value: 'upload_time',
                          label: 'Upload Time',
                        },
                      ]}
                    />
                    {errors.timestamp_field && touched.timestamp_field && (
                      <div className="text-red-500 font-sans text-sm mt-1">
                        {errors.timestamp_field}
                      </div>
                    )}
                  </div>
                );
              },
            },
          ];

          return (
            <Form onSubmit={handleSubmit}>
              <div className="flex flex-row sm:justify-between gap-4 lg:justify-between items-center">
                <div className="flex flex-row justify-start gap-2">
                  <div className="flex justify-start gap-2">
                    <div className="flex items-center">
                      <div className="mx-2">DAC CSV settings</div>
                      <div>
                        <Switch
                          style={{
                            backgroundColor: '#00000040',
                          }}
                          checked={DACCSVSettingsEnabled}
                          onChange={handleRadioChange}
                        />
                      </div>
                    </div>
                  </div>
                  {DACCSVSettingsEnabled && (
                    <div className="text-red-500 font-sans text-sm mt-1 items-center">
                      * Fill all fields below to enable DAC CSV settings
                    </div>
                  )}
                </div>
                {DACCSVSettingsEnabled && (
                  <div className="flex justify-end items-end">
                    <button
                      type="submit"
                      className="bg-[#00D090] hover:bg-[#059669] flex items-center justify-center text-white font-normal p-2 py-2 px-4 rounded-xl focus:outline-none focus:shadow-outline"
                    >
                      <SaveOutlined
                        className={`mr-2`}
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                      />{' '}
                      Save
                    </button>
                  </div>
                )}
              </div>
              {DACCSVSettingsEnabled && (
                <div className="flex justify-center my-4">
                  <Alert
                    message="Start Date Time and Provider columns will be populated with the values below. Both columns should not be present in the uploaded file."
                    type="info"
                    showIcon
                    banner
                  />
                </div>
              )}
              {DACCSVSettingsEnabled && (
                <div className="super-admin-table">
                  <div className="mt-5 ant-table rounded-xl shadow-xl">
                    <SuperAdminTable
                      className={`appointment-page-table`}
                      columns={columns}
                      data={tableData}
                      loading={tableLoader}
                    />
                  </div>
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  ) : (
    <div className="flex justify-center items-center p-4 min-h-[60vh]">
      <Alert
        message="Bulk CSV uploads are currently disabled for this organization."
        type="info"
        showIcon
      />
    </div>
  );
};

export default DACCSVSettings;
